// /**
//  * Calculate payment amount based on hexagon coverage plan.
//  *
//  * Local plans: Starter/Basic/Growth/Pro/Max (5/12/20/30/45 hexagons)
//  * Statewide: All Kerala (₹50k) or Branches (₹25k, flexible count)
//  */

// import type { CoveragePlan } from '../store/types';

// export const LOCAL_PLANS: CoveragePlan[] = [
//   { hexCount: 5, pricePerYear: 1000, planName: 'Starter' },
//   { hexCount: 12, pricePerYear: 2000, planName: 'Basic' },
//   { hexCount: 20, pricePerYear: 3000, planName: 'Growth' },
//   { hexCount: 30, pricePerYear: 4000, planName: 'Pro' },
//   { hexCount: 45, pricePerYear: 5000, planName: 'Max' },
// ];

// export const STATEWIDE_PLANS: CoveragePlan[] = [
//   { hexCount: null, pricePerYear: 50000, planName: 'All Kerala', isAllKerala: true },
//   { hexCount: null, pricePerYear: 25000, planName: 'Branches' },
// ];

// export const COVERAGE_PLANS = LOCAL_PLANS;

// const HEX_AREA_KM2 = 5.16; // H3 Resolution 7 area per hexagon

// export interface PaymentCalculationInput {
//   selectedPlan: CoveragePlan | null;
//   selectedHexagons: string[];
// }

// /**
//  * Calculate payment using the selected plan's price.
//  */
// export const calculatePayment = (input: PaymentCalculationInput): number => {
//   if (!input.selectedPlan) return 0;
//   return input.selectedPlan.pricePerYear;
// };

// /**
//  * Get a detailed payment & coverage breakdown for display.
//  */
// export const getPaymentBreakdown = (input: PaymentCalculationInput) => {
//   const { selectedPlan, selectedHexagons } = input;

//   if (!selectedPlan) {
//     return {
//       planName: 'None',
//       hexLimit: 0,
//       selectedCount: selectedHexagons.length,
//       coverageAreaKm2: selectedHexagons.length * HEX_AREA_KM2,
//       pricePerYear: 0,
//       totalAmount: 0,
//     };
//   }

//   return {
//     planName: selectedPlan.planName,
//     hexLimit: selectedPlan.hexCount ?? 0,
//     selectedCount: selectedHexagons.length,
//     coverageAreaKm2: selectedHexagons.length * HEX_AREA_KM2,
//     pricePerYear: selectedPlan.pricePerYear,
//     totalAmount: selectedPlan.pricePerYear,
//   };
// };


/**
 * Calculate payment amount based on selected plan from backend
 */

import { Plan } from '../apis/partnerForm';

export interface PaymentCalculationInput {
  selectedPlan: Plan | null;
  selectedHexagons: string[];
}

const HEX_AREA_KM2 = 5.16;

export const calculatePayment = (input: PaymentCalculationInput): number => {
  if (!input.selectedPlan) return 0;
  // Add safe navigation for pricing
  if (!input.selectedPlan.pricing && input.selectedPlan.type !== 'custom') return 0;
  
  const isCustom = input.selectedPlan.type === 'custom' || input.selectedPlan.name?.toLowerCase().includes('custom');
  if (isCustom) {
    const costPerHex = input.selectedPlan.costPerHex ?? input.selectedPlan.pricing?.costPerHex ?? 0;
    const hexCount = input.selectedHexagons.length > 0 ? input.selectedHexagons.length : 1;
    const basePrice = hexCount * costPerHex;
    const offer = input.selectedPlan.offer;
    const hasOffer = offer?.isActive ?? false;
    let sellingPrice = basePrice;
    if (hasOffer) {
      if (offer?.type === 'percentage') {
        sellingPrice = basePrice * (1 - (offer.value / 100));
      } else if (offer?.type === 'fixed') {
        sellingPrice = Math.max(0, basePrice - offer.value);
      }
    }
    const gstPercent = input.selectedPlan.pricing?.gstPercentage ?? 18;
    const gstAmount = Math.round(sellingPrice * (gstPercent / 100));
    return sellingPrice + gstAmount;
  }
  
  const pricing = input.selectedPlan.pricing;
  const payInitially = pricing.payInitially ?? 0;
  const sellingPrice = pricing.sellingPrice ?? 0;
  
  const baseForGst = payInitially > 0 ? payInitially : sellingPrice;
  const gstPercent = pricing.gstPercentage ?? 18;
  const gstAmount = Math.round(baseForGst * (gstPercent / 100));
  
  return baseForGst + gstAmount;
};

export const getPaymentBreakdown = (input: PaymentCalculationInput) => {
  const { selectedPlan, selectedHexagons } = input;

  if (!selectedPlan) {
    return {
      planName: 'None',
      hexLimit: 0,
      selectedCount: selectedHexagons.length,
      coverageAreaKm2: selectedHexagons.length * HEX_AREA_KM2,
      pricePerYear: 0,
      totalAmount: 0,
      originalPrice: 0,
      discount: 0,
      gstAmount: 0,
      isTrial: false,
      trialDays: 0,
      offer: null,
      payInitially: 0,
      payAfterSales: 0,
    };
  }

  // Add safe navigation for pricing and features
  const pricing = selectedPlan.pricing || {};
  const features = selectedPlan.features || {};
  const offer = selectedPlan.offer || null;
  const hasOffer = offer?.isActive ?? false;
  
  let originalPrice = pricing.originalPrice ?? 0;
  let sellingPrice = pricing.sellingPrice ?? 0;
  let discount = hasOffer ? originalPrice - sellingPrice : 0;
  let payInitially = pricing.payInitially ?? 0;
  let payAfterSales = pricing.payAfterSales ?? 0;
  let gstPercent = pricing.gstPercentage ?? 18;
  
  const isCustom = selectedPlan.type === 'custom' || selectedPlan.name?.toLowerCase().includes('custom');
  if (isCustom) {
    const costPerHex = selectedPlan.costPerHex ?? pricing.costPerHex ?? 0;
    const hexCount = selectedHexagons.length > 0 ? selectedHexagons.length : 1;
    originalPrice = hexCount * costPerHex;
    sellingPrice = originalPrice;
    if (hasOffer) {
      if (offer?.type === 'percentage') {
        sellingPrice = originalPrice * (1 - (offer.value / 100));
      } else if (offer?.type === 'fixed') {
        sellingPrice = Math.max(0, originalPrice - offer.value);
      }
    }
    discount = originalPrice - sellingPrice;
    payInitially = sellingPrice;
    payAfterSales = 0;
  }
  
  const baseForGst = payInitially > 0 ? payInitially : sellingPrice;
  const gstAmount = Math.round(baseForGst * (gstPercent / 100));
  const calculatedTotal = baseForGst + gstAmount;

  return {
    planName: selectedPlan.displayName || 'Plan',
    hexLimit: selectedPlan.type === 'custom' ? 0 : (features.hexCount ?? 0),
    selectedCount: selectedHexagons.length,
    coverageAreaKm2: selectedHexagons.length * HEX_AREA_KM2,
    pricePerYear: baseForGst,
    totalAmount: calculatedTotal,
    originalPrice: originalPrice,
    discount: discount,
    gstAmount: gstAmount,
    isTrial: selectedPlan.isTrial === 'yes',
    trialDays: selectedPlan.trialDays ?? 0,
    offer: offer,
    payInitially,
    payAfterSales,
  };
};

export const isWithinHexLimit = (plan: Plan | null, selectedCount: number): boolean => {
  if (!plan) return false;
  const hexCount = plan.features?.hexCount ?? 0;
  if (hexCount === 0) return true;
  return selectedCount <= hexCount;
};

export const getRemainingHexSlots = (plan: Plan | null, selectedCount: number): number => {
  if (!plan) return 0;
  const hexCount = plan.features?.hexCount ?? 0;
  if (hexCount === 0) return Infinity;
  return Math.max(0, hexCount - selectedCount);
};